import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import API from "../api";
import { FeatureCollection, Geometry } from "geojson";
import { InferenceProperties } from "../types/inferences";

function useSceneInferences(sceneId?: string, validated?: boolean) {
  const [ isValidating, setIsValidating ] = useState<boolean>(false);
  const [ validationError, setValidationError ] = useState<string>();

  const queryClient = useQueryClient();

  const validate = (validationId: number, isValid: boolean) => {
    if (!sceneId) return;

    setIsValidating(true);
    setValidationError("");

    return API.invalidateInference(sceneId, validationId)
      .then(() => API.validateInference(sceneId, validationId, isValid))
      .then(async () => {
        // Update the inference in the state
        const queryKey = ["scene-inferences", sceneId, validated];
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(
          queryKey,
          (inferenceQueue: FeatureCollection<Geometry, InferenceProperties>) => {
            const features = inferenceQueue.features.map((inference) => {
              if (inference.properties.id !== validationId) return inference;
              return {
                ...inference,
                properties: {
                  something: 1,
                  ...inference.properties,
                  is_valid: isValid
                }
              };
            });

            return {
              ...inferenceQueue,
              features
            };
          }
        );
      })
      .catch((e) => setValidationError(e.message))
      .finally(() => setIsValidating(false));
  };

  const { isPending, error, data, refetch } = useQuery({
    queryKey: ["scene-inferences", sceneId, validated],
    queryFn: () => {
      if (!sceneId) throw Error("Not found.");
      return API.getSceneInferences(sceneId, validated);
    },
    retry: false,
  });

  useEffect(() => {
    // Refresh the cached inferences list when the hook unmounts so users see the
    // correct number of not validated inferences when they enter the page next time.
    return () => {
      refetch();
    };
  }, [refetch]);

  return {
    isPending,
    error,
    inferences: data,
    refetch,
    validate,
    isValidating,
    validationError
  };
}

export default useSceneInferences;
